import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { adminService } from '../../services/adminService';
import {
  FiUsers,
  FiDollarSign,
  FiPackage,
  FiAlertCircle,
  FiTrendingUp,
  FiArrowRight
} from 'react-icons/fi';

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      // Fetch dashboard stats from API
      const response = await adminService.getDashboardStats();
      const apiData = response.data;

      // Fetch recent merchants
      const merchantsResponse = await adminService.getAllMerchants({ page: 1, limit: 3 });
      const recentMerchants = merchantsResponse.data.merchants.map(m => ({
        id: m._id,
        businessName: m.businessName,
        username: m.username,
        email: m.email,
        status: m.subscription.status,
        joinedDate: new Date(m.createdAt).toLocaleDateString('en-GB', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        })
      }));

      // Transform API data to match component structure
      const transformedData = {
        kpis: {
          totalMerchants: apiData.overview.totalMerchants,
          totalRevenue: apiData.revenue.monthly,
          activeSubscriptions: apiData.overview.activeMerchants,
          pendingVerifications: apiData.overview.inactiveMerchants, // Using inactive as pending
          expiringSubscriptions: apiData.alerts.expiringIn5Days
        },
        recentMerchants: recentMerchants,
        subscriptionBreakdown: {
          trial: apiData.overview.freeTrialMerchants,
          base: apiData.packageDistribution.base,
          basicPlus: apiData.packageDistribution.basic_plus
        },
        revenueByMonth: [
          { month: 'This Month', revenue: apiData.revenue.monthly },
          { month: 'Projected', revenue: apiData.revenue.projected },
        ]
      };

      setDashboardData(transformedData);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      // Fallback to mock data if API fails
      const fallbackData = {
        kpis: {
          totalMerchants: 0,
          totalRevenue: 0,
          activeSubscriptions: 0,
          pendingVerifications: 0,
          expiringSubscriptions: 0
        },
        recentMerchants: [],
        subscriptionBreakdown: {
          trial: 0,
          base: 0,
          basicPlus: 0
        },
        revenueByMonth: []
      };
      setDashboardData(fallbackData);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Dashboard">
        <LoadingSpinner fullScreen />
      </AdminLayout>
    );
  }

  const StatCard = ({ title, value, subtitle, icon: Icon, color, onClick }) => (
    <div
      className={`bg-white rounded-lg shadow p-6 border-l-4 ${color} cursor-pointer hover:shadow-lg transition-shadow`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600 uppercase tracking-wide">{title}</p>
          <p className="mt-2 text-3xl font-bold text-gray-900">{value}</p>
          {subtitle && <p className="mt-1 text-sm text-gray-500">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-full ${color.replace('border-', 'bg-').replace('-500', '-100')}`}>
          <Icon className={`w-8 h-8 ${color.replace('border-', 'text-')}`} />
        </div>
      </div>
      <div className="mt-4 flex items-center text-sm text-gray-500">
        <span>View details</span>
        <FiArrowRight className="ml-1 w-4 h-4" />
      </div>
    </div>
  );

  return (
    <AdminLayout title="Dashboard">
      <div className="space-y-6">
        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          <StatCard
            title="Total Merchants"
            value={dashboardData.kpis.totalMerchants}
            subtitle="Registered users"
            icon={FiUsers}
            color="border-blue-500"
            onClick={() => navigate('/admin/merchants')}
          />

          <StatCard
            title="Total Revenue"
            value={`BHD ${dashboardData.kpis.totalRevenue.toFixed(3)}`}
            subtitle="This month"
            icon={FiDollarSign}
            color="border-green-500"
            onClick={() => navigate('/admin/analytics')}
          />

          <StatCard
            title="Active Subscriptions"
            value={dashboardData.kpis.activeSubscriptions}
            subtitle={`${dashboardData.kpis.totalMerchants - dashboardData.kpis.activeSubscriptions} inactive`}
            icon={FiPackage}
            color="border-indigo-500"
            onClick={() => navigate('/admin/subscriptions')}
          />

          <StatCard
            title="Pending Verifications"
            value={dashboardData.kpis.pendingVerifications}
            subtitle="Awaiting approval"
            icon={FiAlertCircle}
            color="border-yellow-500"
            onClick={() => navigate('/admin/merchants?filter=pending')}
          />

          <StatCard
            title="Expiring Soon"
            value={dashboardData.kpis.expiringSubscriptions}
            subtitle="Next 5 days"
            icon={FiTrendingUp}
            color="border-red-500"
            onClick={() => navigate('/admin/subscriptions?filter=expiring')}
          />
        </div>

        {/* Recent Merchants & Subscription Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Merchants */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Recent Merchants</h2>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {dashboardData.recentMerchants.map((merchant) => (
                  <div
                    key={merchant.id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                    onClick={() => navigate(`/admin/merchants/${merchant.id}`)}
                  >
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{merchant.businessName}</p>
                      <p className="text-sm text-gray-500">{merchant.email}</p>
                      <p className="text-xs text-gray-400 mt-1">Joined: {merchant.joinedDate}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        merchant.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {merchant.status}
                      </span>
                      <FiArrowRight className="w-5 h-5 text-gray-400" />
                    </div>
                  </div>
                ))}
              </div>
              <button
                onClick={() => navigate('/admin/merchants')}
                className="mt-4 w-full py-2 text-sm font-medium text-indigo-600 hover:text-indigo-700 transition-colors"
              >
                View All Merchants
              </button>
            </div>
          </div>

          {/* Subscription Breakdown */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Subscription Breakdown</h2>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">Trial</p>
                    <p className="text-sm text-gray-500">30-day free trial</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-blue-600">{dashboardData.subscriptionBreakdown.trial}</p>
                    <p className="text-xs text-gray-500">merchants</p>
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">Base</p>
                    <p className="text-sm text-gray-500">BHD 5.000/month</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-green-600">{dashboardData.subscriptionBreakdown.base}</p>
                    <p className="text-xs text-gray-500">merchants</p>
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-indigo-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">Basic Plus</p>
                    <p className="text-sm text-gray-500">BHD 10.000/month</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-indigo-600">{dashboardData.subscriptionBreakdown.basicPlus}</p>
                    <p className="text-xs text-gray-500">merchants</p>
                  </div>
                </div>
              </div>
              <button
                onClick={() => navigate('/admin/subscriptions')}
                className="mt-4 w-full py-2 text-sm font-medium text-indigo-600 hover:text-indigo-700 transition-colors"
              >
                Manage Subscriptions
              </button>
            </div>
          </div>
        </div>

        {/* Revenue Chart */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Revenue Trend</h2>
          </div>
          <div className="p-6">
            <div className="space-y-3">
              {dashboardData.revenueByMonth.map((item, index) => (
                <div key={index} className="flex items-center space-x-4">
                  <div className="w-16 text-sm font-medium text-gray-600">{item.month}</div>
                  <div className="flex-1">
                    <div className="h-8 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-green-400 to-green-600 rounded-full"
                        style={{ width: `${(item.revenue / 5000) * 100}%` }}
                      />
                    </div>
                  </div>
                  <div className="w-32 text-right text-sm font-semibold text-gray-900">
                    BHD {item.revenue.toFixed(3)}
                  </div>
                </div>
              ))}
            </div>
            <button
              onClick={() => navigate('/admin/analytics')}
              className="mt-4 w-full py-2 text-sm font-medium text-indigo-600 hover:text-indigo-700 transition-colors"
            >
              View Detailed Analytics
            </button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboardPage;
